import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RefreshCw, Send, Loader2, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../api";

const REFRESH_MS = 30000;

function fmtAge(min) {
  if (min == null) return "—";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const r = min % 60;
  return `${h}h${String(r).padStart(2, "0")}`;
}

function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" });
  } catch { return s; }
}

export default function AdminInscricoesPendentes() {
  const [data, setData] = useState({ pending: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await adminApi.listInscricoesPendentes();
      setData(r);
    } catch (e) {
      toast.error(e.message || "Erro ao carregar pendentes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const forceSend = async (linkId) => {
    if (!window.confirm("Confirma o envio manual deste link agora?")) return;
    setSendingId(linkId);
    try {
      const r = await adminApi.forceSendInscricao(linkId);
      if (r.ok && r.result?.email_sent) {
        toast.success("Email enviado com sucesso ao comprador.");
      } else if (r.result?.already_sent) {
        toast.info("Email já tinha sido marcado como enviado.");
      } else {
        toast.warning(`Falha no envio: ${r.result?.reason || "SMTP devolveu erro"}`);
      }
      await load();
    } catch (e) {
      toast.error(e.message || "Erro ao forçar envio");
    } finally {
      setSendingId(null);
    }
  };

  const totalCritical = data.pending.filter((r) => r.AgeMinutes > 10).length;

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="admin-inscricoes-pendentes">
      <div className="flex items-center justify-between mb-1">
        <div>
          <h1 className="text-2xl font-bold text-[#0F1829] flex items-center gap-2">
            <AlertTriangle className="text-amber-600" size={22} /> Inscrições com email pendente
          </h1>
          <p className="text-sm text-[#978D78] mt-1">
            Links de inscrição criados nas últimas 72 h cujo email automático ao comprador ainda não foi enviado.
            O safety-net re-tenta a cada 2 min. Esta página actualiza-se a cada 30 s.
          </p>
        </div>
        <button onClick={load} disabled={loading}
          data-testid="admin-refresh-pendentes"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded border border-[#E6E1D6] hover:bg-[#FAF8F2] transition">
          {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          Refrescar
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mt-4 mb-6">
        <div className="p-3 bg-white border border-[#E6E1D6] rounded-md" data-testid="kpi-total">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#978D78] font-bold">Pendentes (72h)</p>
          <p className="text-2xl font-bold text-[#0F1829] mt-1">{data.total}</p>
        </div>
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-md" data-testid="kpi-critical">
          <p className="text-[10px] uppercase tracking-[0.18em] text-amber-900 font-bold">Críticos (&gt;10 min)</p>
          <p className="text-2xl font-bold text-amber-800 mt-1">{totalCritical}</p>
        </div>
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md" data-testid="kpi-status">
          <p className="text-[10px] uppercase tracking-[0.18em] text-emerald-900 font-bold">Estado safety-net</p>
          <p className="text-sm text-emerald-800 mt-1 flex items-center gap-1.5"><CheckCircle2 size={14} /> A correr (cada 2 min)</p>
        </div>
      </div>

      {data.total === 0 ? (
        <div className="p-8 text-center bg-emerald-50 border border-emerald-200 rounded-md" data-testid="empty-state">
          <CheckCircle2 className="mx-auto text-emerald-600 mb-2" size={32} />
          <p className="text-emerald-900 font-medium">Nenhuma inscrição pendente.</p>
          <p className="text-xs text-emerald-800/70 mt-1">Todos os links de inscrição das últimas 72 h enviaram email correctamente.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E6E1D6] rounded-md overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-[#FAF8F2] text-[10px] uppercase tracking-[0.18em] text-[#978D78]">
              <tr>
                <th className="text-left px-3 py-2 font-bold">Idade</th>
                <th className="text-left px-3 py-2 font-bold">Encomenda</th>
                <th className="text-left px-3 py-2 font-bold">Curso</th>
                <th className="text-left px-3 py-2 font-bold">Comprador</th>
                <th className="text-left px-3 py-2 font-bold">Criado em</th>
                <th className="text-right px-3 py-2 font-bold">Acção</th>
              </tr>
            </thead>
            <tbody>
              {data.pending.map((r) => {
                const critical = r.AgeMinutes > 10;
                return (
                  <tr key={r.ID_LinkInscricao} data-testid={`pending-row-${r.ID_LinkInscricao}`}
                      className={`border-t border-[#E6E1D6] ${critical ? "bg-amber-50/40" : ""}`}>
                    <td className="px-3 py-2 font-mono text-xs">
                      <span className={critical ? "text-amber-800 font-bold" : "text-[#0F1829]"}>
                        {fmtAge(r.AgeMinutes)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <Link to={`/admin/t/encomenda/Tinf_Encomenda/view/${r.ID_Encomenda}`}
                        className="text-[#0F1829] hover:underline inline-flex items-center gap-1">
                        #{r.ID_Encomenda} <ExternalLink size={11} />
                      </Link>
                      <span className="ml-1 text-[10px] font-mono text-[#978D78]">{r.ID_EstadoEncomenda}</span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-[#0F1829]">{r.ProdutoNome || `Curso #${r.ID_Curso}`}</div>
                      <div className="text-[10px] text-[#978D78]">
                        {r.CursoData && new Date(r.CursoData).toLocaleDateString("pt-PT")}
                        {r.CursoLocal && ` · ${r.CursoLocal}`}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-[#0F1829]">{r.BuyerName || "—"}</div>
                      <div className="text-[10px] text-[#978D78]">{r.BuyerEmail || "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-xs text-[#0F1829]">{fmtDate(r.CriadoEm)}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => forceSend(r.ID_LinkInscricao)}
                        disabled={sendingId === r.ID_LinkInscricao}
                        data-testid={`force-send-${r.ID_LinkInscricao}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-50 transition">
                        {sendingId === r.ID_LinkInscricao ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <Send size={12} />
                        )}
                        Forçar envio
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
